import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Bot, RefreshCw, Mail, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', saffron: '#FF9933' };

const fmtWhen = ts => (ts ? new Date(ts).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtLog = ts => (ts ? new Date(ts).toLocaleTimeString('en-AU', { hour12: false }) : '--:--:--');

const resultLine = r => {
  const res = r.result || {};
  const parts = [];
  if (res.aria_actions != null) parts.push(`${res.aria_actions.toLocaleString()} actions`);
  if (res.auto_offered != null) parts.push(`${res.auto_offered} auto-offered`);
  if (res.suggested != null) parts.push(`${res.suggested} suggested`);
  if (res.claims_scanned != null) parts.push(`${res.claims_scanned.toLocaleString()} scanned`);
  if (res.expired != null) parts.push(`${res.expired} expired`);
  if (res.new_escalations) parts.push(`${res.new_escalations} escalated`);
  return parts.join(' | ') || 'completed';
};

const domainCode = label => {
  const l = (label || '').toLowerCase();
  if (l.includes('renewal')) return { code: 'ARIA-REN', color: '#4ADE9F' };
  if (l.includes('diary') || l.includes('claim')) return { code: 'ARIA-CLM', color: C.teal };
  if (l.includes('fraud') || l.includes('siu')) return { code: 'ARIA-SIU', color: C.saffron };
  if (l.includes('underwrit') || l.includes('stp') || l.includes('pipeline')) return { code: 'ARIA-UW', color: '#6CB4FF' };
  if (l.includes('compliance') || l.includes('aixiom')) return { code: 'AIXIOM-C', color: '#C9A0FF' };
  return { code: 'ARIA-OPS', color: 'rgba(255,255,255,0.7)' };
};

const Stat = ({ label, value, color, testid }) => (
  <div className="flex flex-col" data-testid={testid}>
    <span className="text-3xl font-light font-mono tracking-tight" style={{ color, fontVariantNumeric: 'tabular-nums' }}>{Number(value || 0).toLocaleString()}</span>
    <span className="text-[10px] uppercase tracking-[0.15em] mt-1" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Georgia, serif' }}>{label}</span>
  </div>
);

const ExceptionChip = ({ label, n, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-mono font-semibold transition-colors hover:brightness-125"
    style={{ background: n > 0 ? 'rgba(226,75,74,0.15)' : 'rgba(29,158,117,0.12)', color: n > 0 ? '#FF9C9B' : '#7FDDB9', border: `1px solid ${n > 0 ? 'rgba(226,75,74,0.35)' : 'rgba(29,158,117,0.3)'}` }}>
    {label}:{n}
  </button>
);

const TriggerChip = ({ t }) => (
  <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold" title={t.detail}
    style={{ background: 'rgba(226,75,74,0.12)', color: '#FF9C9B', border: '1px solid rgba(226,75,74,0.3)' }}>{t.code}</span>
);

function UtcClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  return (
    <span className="font-mono text-[11px]" style={{ color: 'rgba(255,255,255,0.55)', fontVariantNumeric: 'tabular-nums' }} data-testid="mission-clock">
      {now.toISOString().slice(11, 19)} UTC
    </span>
  );
}

export const AriaAutonomy = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [busy, setBusy] = useState('');
  const [showActivity, setShowActivity] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    api.get('/governance/feed').then(r => setFeed(r.data)).catch(e => console.error('autonomy feed:', e?.message));
    api.get('/governance/escalations?domain=renewal&status=open').then(r => setEscalations(r.data.escalations || [])).catch(() => {});
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 15000);
    return () => clearInterval(id);
  }, [load]);

  const runAutonomy = async () => {
    setBusy('run');
    try {
      const { data } = await api.post('/governance/renewals/autonomy/run');
      toast.success(`ARIA processed renewals — ${data.auto_offered} auto-offered, ${data.exception_renewals} exception(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Run failed'); }
    setBusy('');
  };

  const sendBrief = async () => {
    setBusy('brief');
    try {
      const { data } = await api.post('/governance/weekly-brief/send');
      toast.success(data.sent ? `Weekly brief sent to ${data.recipients} exec(s)` : `Not sent — ${data.reason}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    setBusy('');
  };

  const resolve = async (id) => {
    try {
      await api.post(`/governance/escalations/${id}/resolve`, { note });
      toast.success('Escalation resolved');
      setResolving(null); setNote('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resolve failed'); }
  };

  if (!feed) return null;
  const t = feed.autonomous_today || {};
  const ex = feed.open_exceptions || {};
  const activity = feed.recent_activity || [];

  return (
    <Card className="rounded-sm overflow-hidden relative" style={{ background: C.navy, border: '1px solid rgba(255,255,255,0.1)' }} data-testid="aria-autonomy-card">
      {/* Subtle mission-control dot grid */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />

      {/* Status bar */}
      <div className="relative flex items-center justify-between px-5 py-2.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.25)' }}>
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#4ADE9F' }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: '#4ADE9F' }} />
          </span>
          <Bot className="w-3.5 h-3.5" style={{ color: '#4ADE9F' }} />
          <span className="text-[11px] font-mono font-semibold uppercase tracking-[0.2em]" style={{ color: 'white' }}>ARIA Mission Control</span>
          <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-mono font-bold" style={{ background: 'rgba(74,222,159,0.15)', color: '#4ADE9F', border: '1px solid rgba(74,222,159,0.35)' }}>LIVE</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>poll 15s</span>
          <UtcClock />
        </div>
      </div>

      <div className="relative p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Georgia, serif' }}>
              AI runs it — you see the exceptions
            </p>
            <div className="flex flex-wrap items-end gap-8 mt-4">
              <Stat label="Diary actions (last sweep)" value={t.claims_diary_actions} color={C.teal} testid="autonomy-diary-actions" />
              <Stat label="Renewals auto-offered" value={t.renewals_auto_offered} color="#4ADE9F" testid="autonomy-renewals-offered" />
              <Stat label="STP bound today" value={t.underwriting_stp_bound} color={C.saffron} testid="autonomy-stp-bound" />
              <Stat label="Claims fraud-scanned" value={t.fraud_scanned} color="white" testid="autonomy-fraud-scanned" />
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-5">
              <span className="text-[10px] uppercase tracking-[0.15em] mr-1" style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Georgia, serif' }}>Open exceptions</span>
              <ExceptionChip label="CLM" n={ex.claims} onClick={() => navigate('/claims')} testid="exceptions-claims" />
              <ExceptionChip label="REN" n={ex.renewals} testid="exceptions-renewals" />
              <ExceptionChip label="UW" n={ex.underwriting} onClick={() => navigate('/pipeline')} testid="exceptions-underwriting" />
              <ExceptionChip label="SIU" n={ex.fraud_suggestions} onClick={() => navigate('/claims')} testid="exceptions-fraud" />
              <ExceptionChip label="CND" n={ex.conduct} onClick={() => navigate('/distribution?tab=conduct')} testid="exceptions-conduct" />
            </div>
          </div>
          <div className="flex flex-col gap-2 lg:items-end shrink-0">
            <Button onClick={runAutonomy} disabled={!!busy} size="sm" data-testid="run-renewal-autonomy-btn" style={{ background: '#4ADE9F', color: C.navy }}>
              {busy === 'run' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />} Run Renewal Autonomy
            </Button>
            <Button onClick={sendBrief} disabled={!!busy} size="sm" variant="outline" data-testid="send-weekly-brief-btn"
              className="bg-transparent text-white border-white/25 hover:bg-white/10 hover:text-white">
              <Mail className="w-4 h-4 mr-2" /> {busy === 'brief' ? 'Sending…' : 'Send Weekly Brief'}
            </Button>
            <p className="text-[10px] text-right font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>
              renewals sweep daily | brief mondays<br />last run {fmtWhen(feed.last_runs?.renewals)}
            </p>
          </div>
        </div>

        {escalations.length > 0 && (
          <div className="mt-5 rounded-sm p-4" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(226,75,74,0.25)' }} data-testid="renewal-exceptions-table">
            <p className="text-xs font-mono font-semibold mb-2" style={{ color: '#FF9C9B' }}>▲ {escalations.length} renewal exception(s) awaiting a C-suite decision</p>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {escalations.map(e => (
                <div key={e.escalation_id} className="flex flex-wrap items-center gap-2 text-xs py-1.5 border-b border-white/5 last:border-0" data-testid={`renewal-escalation-${e.escalation_id}`}>
                  <span className="font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>{e.reference}</span>
                  <span className="font-medium text-white flex-1 min-w-[140px]">{e.business_name}</span>
                  <span style={{ color: 'rgba(255,255,255,0.6)' }}>{e.insurance_class}</span>
                  <span className="font-mono" style={{ color: C.saffron }}>${Number(e.amount || 0).toLocaleString()}</span>
                  {(e.triggers || []).map((tr, i) => <TriggerChip key={i} t={tr} />)}
                  {resolving === e.escalation_id ? (
                    <span className="flex items-center gap-1.5">
                      <Input value={note} onChange={ev => setNote(ev.target.value)} placeholder="Decision note…" className="h-7 w-44 text-xs bg-white/10 border-white/20 text-white" data-testid="resolve-note-input" />
                      <Button size="sm" className="h-7 px-2 text-xs" style={{ background: C.green }} onClick={() => resolve(e.escalation_id)} data-testid="resolve-confirm-btn">✓</Button>
                      <button className="text-white/50 text-xs" onClick={() => setResolving(null)}>✕</button>
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-transparent text-white border-white/25 hover:bg-white/10 hover:text-white"
                      onClick={() => { setResolving(e.escalation_id); setNote(''); }} data-testid={`resolve-escalation-${e.escalation_id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Resolve
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live terminal log */}
        <button onClick={() => setShowActivity(s => !s)} className="flex items-center gap-1 text-xs mt-5 font-mono" style={{ color: 'rgba(255,255,255,0.55)' }} data-testid="toggle-activity-feed">
          {showActivity ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          ops.log — live activity ({activity.length})
        </button>
        {showActivity && (
          <div className="mt-2 rounded-sm overflow-hidden" style={{ background: '#060F1A', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="activity-feed">
            <div className="flex items-center gap-1.5 px-3 py-1.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <span className="w-2 h-2 rounded-full" style={{ background: '#E24B4A' }} />
              <span className="w-2 h-2 rounded-full" style={{ background: '#E8A020' }} />
              <span className="w-2 h-2 rounded-full" style={{ background: '#1D9E75' }} />
              <span className="ml-2 text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>aria@fraime:~/ops$ tail -f autonomy.log</span>
            </div>
            <div className="px-3 py-2 max-h-64 overflow-y-auto font-mono text-[11px] leading-[1.9]">
              {activity.length === 0 && (
                <p style={{ color: 'rgba(255,255,255,0.4)' }}>&gt; awaiting first autonomous sweep…</p>
              )}
              {activity.map((r, i) => {
                const d = domainCode(r.label);
                return (
                  <div key={i} className="flex items-baseline gap-2 whitespace-nowrap overflow-hidden" style={{ opacity: i === 0 ? 1 : Math.max(0.45, 1 - i * 0.07) }}>
                    <span style={{ color: 'rgba(255,255,255,0.35)', fontVariantNumeric: 'tabular-nums' }}>[{fmtLog(r.timestamp)}]</span>
                    <span className="font-bold" style={{ color: d.color }}>{d.code}</span>
                    <span style={{ color: 'rgba(255,255,255,0.45)' }}>▸</span>
                    <span style={{ color: 'rgba(255,255,255,0.85)' }}>{r.label.toLowerCase()}</span>
                    <span className="truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>— {resultLine(r)}</span>
                  </div>
                );
              })}
              <div className="flex items-center gap-1">
                <span style={{ color: '#4ADE9F' }}>&gt;</span>
                <span className="term-cursor inline-block w-[7px] h-[13px]" style={{ background: '#4ADE9F' }} />
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AriaAutonomy;
